/**
 * Timestamp Wins Strategy - More recent savedAt wins.
 *
 * Used as a fallback when sequence numbers are unreliable (e.g., clock skew
 * across devices caused sequences to diverge). savedAt is the client epoch ms
 * recorded at the moment saveProject() was called.
 *
 * Limitation: client clocks are not trustworthy across devices. This strategy
 * should only be used when sequence-based resolution is inconclusive.
 */

import type { ReconciliationStrategy, ReconciliationContext, ReconciliationResult } from "./reconciliationStrategy";
import { registerStrategy } from "./reconciliationStrategy";

class TimestampWinsStrategyImpl implements ReconciliationStrategy {
  readonly id = "timestamp-wins";

  describe(): string {
    return "More recent savedAt timestamp wins. Tie (±1s) resolves to local.";
  }

  resolve(ctx: ReconciliationContext): ReconciliationResult {
    const { local, remote } = ctx;
    const TOLERANCE_MS = 1_000; // treat timestamps within 1s as equal

    const diff = local.savedAt - remote.savedAt;

    if (diff > TOLERANCE_MS) {
      return {
        winner:  "local",
        payload: local,
        reason:  `Local savedAt ${local.savedAt} is ${diff}ms newer than remote ${remote.savedAt}`,
      };
    }

    if (diff < -TOLERANCE_MS) {
      return {
        winner:  "remote",
        payload: remote,
        reason:  `Remote savedAt ${remote.savedAt} is ${Math.abs(diff)}ms newer than local ${local.savedAt}`,
      };
    }

    // Within tolerance - local wins
    return {
      winner:  "local",
      payload: local,
      reason:  `Timestamps within tolerance (${TOLERANCE_MS}ms) - local preserved`,
    };
  }
}

export const timestampWinsStrategy = new TimestampWinsStrategyImpl();
registerStrategy(timestampWinsStrategy);
